import * as XLSX from 'xlsx';
import CurrencyUtils from './currency.utils';

// Helpers puros de la carga masiva de servicios de clínica (035-clinic-services-bulk-import).

const COLUMN_ALIASES = {
  title: ['servicio', 'nombre', 'titulo'],
  price: ['precio final', 'precio'],
  coverage: ['cobertura pawer a la que corresponde', 'cobertura pawer', 'cobertura']
};
// Sin fila de encabezados se asume el orden de la planilla original.
const DEFAULT_COLUMNS = { title: 0, price: 1, coverage: 2 };

// CLP no lleva decimales: un separador seguido de 1 o 2 dígitos al final es un decimal
// ("18.990,50", "18990.5"), y solo se aceptan miles agrupados de a 3 (research §2).
const DECIMAL_TAIL = /[.,]\d{1,2}$/;
const WHOLE_AMOUNT = /^(\d+|\d{1,3}([.,]\d{3})+)$/;

// `raw: true` deja los precios como texto: sin él "18.990" puede leerse como 18.99.
const READ_OPTIONS = { raw: true };
const SHEET_OPTIONS = { header: 1, defval: '', raw: false, blankrows: false };

function normalizeHeader(cell) {
  return String(cell ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function isBlankRow(row) {
  return row.every(cell => String(cell ?? '').trim() === '');
}

export function parsePrice(text) {
  const raw = String(text ?? '').replace(/[$\s]/g, '');
  if (!/\d/.test(raw)) return { value: null, error: 'PRICE_REQUIRED' };
  if (!WHOLE_AMOUNT.test(raw)) {
    const isDecimal = DECIMAL_TAIL.test(raw) && WHOLE_AMOUNT.test(raw.replace(DECIMAL_TAIL, ''));
    return { value: null, error: isDecimal ? 'PRICE_HAS_DECIMALS' : 'PRICE_INVALID' };
  }

  const value = CurrencyUtils.parseAmountInput(raw, false);
  if (!(value > 0)) return { value: null, error: 'PRICE_NOT_POSITIVE' };
  return { value, error: null };
}

// Índice de cada columna según los encabezados, o null si la fila no es de encabezados.
export function detectColumns(headerRow) {
  const headers = headerRow.map(normalizeHeader);
  const columns = {};
  Object.entries(COLUMN_ALIASES).forEach(([key, aliases]) => {
    const index = headers.findIndex(h => aliases.includes(h));
    if (index !== -1) columns[key] = index;
  });
  if (!Object.keys(columns).length) return null;
  return { ...DEFAULT_COLUMNS, ...columns };
}

export function rowsFromMatrix(matrix) {
  if (!matrix.length) return [];

  const headerColumns = detectColumns(matrix[0]);
  const columns = headerColumns || DEFAULT_COLUMNS;
  const firstDataLine = headerColumns ? 1 : 0;
  const rows = [];

  matrix.slice(firstDataLine).forEach((row, offset) => {
    if (isBlankRow(row)) return;
    rows.push({
      id: rows.length + 1,
      sourceLine: firstDataLine + offset + 1,
      title: String(row[columns.title] ?? ''),
      priceText: String(row[columns.price] ?? ''),
      coverage: String(row[columns.coverage] ?? '').trim(),
      serverErrors: []
    });
  });
  return rows;
}

function rowsFromWorkbook(workbook) {
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  return rowsFromMatrix(XLSX.utils.sheet_to_json(sheet, SHEET_OPTIONS));
}

// Texto pegado desde Google Sheets (TSV) o un CSV; xlsx detecta el separador.
export function readImportText(text) {
  if (!String(text ?? '').trim()) return [];
  return rowsFromWorkbook(XLSX.read(text, { ...READ_OPTIONS, type: 'string' }));
}

function readFileAs(file, method) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader[method](file);
  });
}

// Un .csv se lee como texto: leído como bytes, "Desparasitación" llega como
// "DesparasitaciÃ³n" (research §1).
export async function readImportFile(file) {
  if (/\.csv$/i.test(file.name)) {
    return readImportText(await readFileAs(file, 'readAsText'));
  }
  const buffer = await readFileAs(file, 'readAsArrayBuffer');
  return rowsFromWorkbook(XLSX.read(new Uint8Array(buffer), { ...READ_OPTIONS, type: 'array' }));
}

// Mensajes de los códigos del cliente y de los `errors[].code` del 422 (research §6).
const ERROR_MESSAGES = {
  TITLE_REQUIRED: 'El título es obligatorio.',
  PRICE_REQUIRED: 'El precio es obligatorio.',
  PRICE_HAS_DECIMALS: 'El precio tiene decimales; en CLP debe ser un número entero.',
  PRICE_INVALID: 'El precio no es un número válido.',
  PRICE_NOT_POSITIVE: 'El precio debe ser mayor a 0.',
  COVERAGE_REQUIRED: 'La fila no tiene cobertura Pawer.',
  DUPLICATE_TITLE: 'Hay otra fila con este mismo título.',
  PRICING_REQUIRED: 'El precio es obligatorio.',
  INVALID_PRICING: 'El precio debe ser un número entero mayor a 0.',
  SERVICE_IDS_REQUIRED: 'Asigna un servicio Pawer a esta cobertura.',
  INVALID_SERVICE_ID: 'El servicio Pawer asignado no existe. Elige otro.',
  SERVICE_NOT_FOUND: 'El servicio Pawer asignado no existe. Elige otro.',
  DUPLICATE_SERVICE_IDS: 'El servicio Pawer está repetido.',
  INVALID_ITEM: 'La fila tiene un formato inválido.',
  DUPLICATE_TITLE_IN_PAYLOAD: 'Hay otra fila con este mismo título.',
  TITLE_ALREADY_EXISTS: 'La clínica ya tiene un servicio con este título.'
};

export function errorMessage(code) {
  return ERROR_MESSAGES[code] || `La fila fue rechazada (${code}).`;
}

export function groupCoverages(rows) {
  return [...new Set(rows.map(row => row.coverage).filter(Boolean))];
}

export function buildBulkItems(rows, assignments) {
  return rows.map(row => ({
    title: row.title.trim(),
    pricing: parsePrice(row.priceText).value,
    serviceIds: [assignments[row.coverage]]
  }));
}

// Errores del cliente por id de fila. La cobertura sin asignar no es un error de fila:
// bloquea la confirmación desde el selector de su cobertura.
export function validateRows(rows) {
  const titleCount = new Map();
  rows.forEach(row => {
    const title = row.title.trim();
    if (title) titleCount.set(title, (titleCount.get(title) || 0) + 1);
  });

  const errors = {};
  rows.forEach(row => {
    const title = row.title.trim();
    const rowErrors = [];
    if (!title) rowErrors.push('TITLE_REQUIRED');
    const { error: priceError } = parsePrice(row.priceText);
    if (priceError) rowErrors.push(priceError);
    if (!row.coverage) rowErrors.push('COVERAGE_REQUIRED');
    if (titleCount.get(title) > 1) rowErrors.push('DUPLICATE_TITLE');
    errors[row.id] = rowErrors;
  });
  return errors;
}

// `errors[].index` es la posición en los `items` enviados; `sentRowIds[index]` es la fila.
export function mapServerErrors(errors, sentRowIds) {
  const byRow = {};
  (errors || []).forEach(({ index, code }) => {
    const rowId = sentRowIds[index];
    if (rowId === undefined) return;
    (byRow[rowId] = byRow[rowId] || []).push(code);
  });
  return byRow;
}
